import type { Hash, WalletClient, TransactionRequest, RpcTransactionRequest } from "viem";

type ExtendedWalletClient = WalletClient & {
    request: (args: { method: "eth_call"; params: [RpcTransactionRequest, "latest"] }) => Promise<string>;
};

// Helper function to estimate gas for a transaction
export const estimateContractTransactionGas = async (
    walletClient: WalletClient,
    transactionRequest: TransactionRequest,
): Promise<bigint> => {
    if (!walletClient.account) throw new Error("Wallet account not provided");

    const result = await walletClient.request({
        method: "eth_estimateGas",
        params: [{ ...transactionRequest, from: walletClient.account.address } as RpcTransactionRequest],
    });

    if (!result) throw new Error("Failed to estimate gas");

    return BigInt(result);
};

export const callContractMethod = async (
    walletClient: WalletClient,
    transactionRequest: TransactionRequest,
): Promise<string> => {
    if (!walletClient.account) throw new Error("Wallet account not provided");

    const result = await (walletClient as ExtendedWalletClient).request({
        method: "eth_call",
        params: [{ ...transactionRequest, from: walletClient.account.address } as RpcTransactionRequest, "latest"],
    });

    return result as string;
};

// Helper function to gateway a transaction request
export const writeContractTransaction = async (
    walletClient: WalletClient,
    transactionRequest: TransactionRequest,
): Promise<Hash> => {
    if (!walletClient.account) throw new Error("Wallet account not provided");

    // Extract the contract address from the transaction request
    if (!transactionRequest.to) throw new Error("Transaction request missing 'to' address");

    // Send the transaction directly using the request method
    const hash = await walletClient.request({
        method: "eth_sendTransaction",
        params: [
            {
                from: walletClient.account.address,
                to: transactionRequest.to,
                data: transactionRequest.data,
            },
        ],
    });

    return hash as Hash;
};

// Helper function to estimate gas for a multicall transaction
export const estimateMulticallGas = async (
    walletClient: WalletClient,
    transactions: TransactionRequest[],
): Promise<bigint> => {
    if (!walletClient.account) throw new Error("Wallet account not provided");
    if (transactions.length === 0) throw new Error("No transactions provided for multicall");
    if (!transactions[0].to) throw new Error("First transaction missing 'to' address");

    // Estimate gas for the multicall
    const result = await walletClient.request({
        method: "eth_estimateGas",
        params: [
            {
                from: walletClient.account.address,
                to: transactions[0].to,
                data: `0x${transactions.map((tx) => (tx.data || "0x").slice(2)).join("")}` as `0x${string}`,
            },
        ],
    });

    return BigInt(result as string);
};

// Helper function to read a single contract method
export const readContractMethod = async (
    walletClient: WalletClient,
    transactionRequest: TransactionRequest,
): Promise<string> => {
    if (!walletClient.account) throw new Error("Wallet account not provided");
    if (!transactionRequest.to) throw new Error("Transaction request missing 'to' address");

    try {
        const result = await (walletClient as ExtendedWalletClient).request({
            method: "eth_call",
            params: [
                {
                    from: walletClient.account.address,
                    to: transactionRequest.to,
                    data: transactionRequest.data,
                },
                "latest",
            ],
        });

        return result as string;
    } catch (error) {
        console.trace("Error in readContractMethod:", error);
        return "0x0";
    }
};
